// src/components/TransactionCardList.js
import React from 'react';

const getStatusBadge = (status) => {
  switch (status?.toLowerCase()) {
    case 'completed':
    case 'approved':
      return 'bg-green-500 text-white';
    case 'pending':
      return 'bg-yellow-500 text-white';
    case 'rejected':
    case 'cancelled':
      return 'bg-red-500 text-white';
    default:
      return 'bg-gray-500 text-white';
  }
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Format large numbers with commas
const formatNumber = (num) => {
  return parseFloat(num || 0).toLocaleString();
};

const TransactionCardList = ({ transactions, onClickCard }) => {
  return (
    <div className="rounded-lg border border-gray-700">
      <div className="block w-full">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-600">
            <thead className="bg-gray-800 hidden sm:table-header-group">
              <tr>
                <th scope="col" className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider whitespace-nowrap">
                  Txn ID
                </th>
                <th scope="col" className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Game
                </th>
                <th scope="col" className="px-3 sm:px-4 py-3 text-right text-xs font-medium text-gray-300 uppercase tracking-wider whitespace-nowrap">
                  Gems
                </th>
                <th scope="col" className="px-3 sm:px-4 py-3 text-right text-xs font-medium text-gray-300 uppercase tracking-wider hidden md:table-cell">
                  Receiver Coin
                </th>
                <th scope="col" className="px-3 sm:px-4 py-3 text-center text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-3 sm:px-4 py-3 text-right text-xs font-medium text-gray-300 uppercase tracking-wider hidden md:table-cell">
                  Date
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700 bg-gray-900">
              {transactions.map((txn) => (
                <React.Fragment key={txn.id}>
                  {/* Mobile View */}
                  <div 
                    onClick={() => onClickCard(txn)}
                    className="block sm:hidden p-3 hover:bg-gray-800 cursor-pointer transition-colors border-b border-gray-700"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <div className="text-sm font-medium text-white mb-1">
                          {txn.gameName || 'N/A'}
                        </div>
                        <div className="text-xs text-gray-400 mb-2">
                          ID: {txn.transactionId?.substring(0, 8) + '...' || 'N/A'}
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="text-sm font-medium text-white">
                          {formatNumber(txn.coin)} Gems
                        </span>
                        <div className="mt-1">
                          <span className={`px-2 py-0.5 inline-flex text-[10px] leading-4 font-semibold rounded-full ${getStatusBadge(txn.status)}`}>
                            {txn.status?.charAt(0).toUpperCase() + txn.status?.slice(1) || 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-between items-center text-xs text-gray-400">
                      <span>{formatDate(txn.createdAt)}</span>
                      <span>{formatNumber(txn.receiverCoin)} Coins</span>
                    </div>
                  </div>
                  
                  {/* Desktop View */}
                  <tr 
                    onClick={() => onClickCard(txn)}
                    className="hidden sm:table-row hover:bg-gray-800 cursor-pointer transition-colors"
                  >
                    <td className="px-3 sm:px-4 py-3 text-sm text-gray-300 font-medium whitespace-nowrap">
                      <div className="flex flex-col">
                        <span className="text-xs text-gray-400 sm:hidden">Txn ID</span>
                        <span className="truncate max-w-[120px] sm:max-w-none">
                          {txn.transactionId || 'N/A'}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 sm:px-4 py-3 text-sm text-gray-300">
                      <div className="flex flex-col">
                        <span className="text-xs text-gray-400 sm:hidden">Game</span>
                        <span className="truncate">
                          {txn.gameName || 'N/A'}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 sm:px-4 py-3 text-sm text-gray-300 text-right whitespace-nowrap">
                      <div className="flex flex-col">
                        <span className="text-xs text-gray-400 sm:hidden">Gems</span>
                        <span>{formatNumber(txn.coin)}</span>
                      </div>
                    </td>
                    <td className="px-3 sm:px-4 py-3 text-sm text-gray-300 text-right hidden md:table-cell">
                      <div className="flex flex-col">
                        <span className="text-xs text-gray-400 md:hidden">Receiver</span>
                        <span>{formatNumber(txn.receiverCoin)}</span>
                      </div>
                    </td>
                    <td className="px-3 sm:px-4 py-3 text-sm text-center whitespace-nowrap">
                      <div className="flex flex-col items-center">
                        <span className="text-xs text-gray-400 sm:hidden mb-1">Status</span>
                        <span className={`px-2 py-1 inline-flex text-xs leading-4 font-semibold rounded-full ${getStatusBadge(txn.status)} min-w-[80px] justify-center`}>
                          {txn.status?.charAt(0).toUpperCase() + txn.status?.slice(1) || 'N/A'}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 sm:px-4 py-3 text-sm text-gray-300 text-right hidden md:table-cell">
                      <div className="flex flex-col">
                        <span className="text-xs text-gray-400 md:hidden">Date</span>
                        <span className="whitespace-nowrap">{formatDate(txn.createdAt)}</span>
                      </div>
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
    );
};

export default TransactionCardList;
